import { useLingui } from '@lingui/react'
import { Box, ButtonBase, Typography, useTheme } from '@mui/material'
import { alpha } from '@mui/material/styles'
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar'
import GroupIcon from '@mui/icons-material/Group'
import SettingsIcon from '@mui/icons-material/Settings'
import { formatHistoryCountDisplay } from '../../utils/format-history-count'
import { NOTIFICATION_TAB_ITEMS, NOTIFICATION_TABS } from './notification-center.const'

const TAB_ICONS = {
  [NOTIFICATION_TABS.CRASHES]: DirectionsCarIcon,
  [NOTIFICATION_TABS.USERS]: GroupIcon,
  [NOTIFICATION_TABS.SYSTEM]: SettingsIcon,
}

function NotificationTabLabel({ tabId, label }) {
  const Icon = TAB_ICONS[tabId]
  return (
    <Typography sx={{ fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {Icon && <Icon sx={{ fontSize: 16 }} />}
      {label}
    </Typography>
  )
}

function NotificationTabButton({ tabId, label, count, isActive, isUnreadTab, onClick }) {
  const theme = useTheme()
  const activeBackground = theme.palette.newColors?.gray ?? theme.palette.primary.dark
  const hasUnreadHighlight = isUnreadTab && count > 0

  return (
    <ButtonBase
      onClick={onClick}
      aria-pressed={isActive}
      sx={{
        flex: '1 0 auto',
        minWidth: 64,
        flexDirection: 'column',
        px: 1.5,
        py: 0.75,
        borderRadius: 2,
        border: `1px solid ${isActive ? activeBackground : theme.palette.divider}`,
        bgcolor: isActive ? activeBackground : 'background.paper',
        color: isActive ? theme.palette.common.white : 'text.primary',
        transition: theme.transitions.create(['background-color', 'border-color'], { duration: theme.transitions.duration.shorter }),
        '&:hover': { bgcolor: isActive ? activeBackground : alpha(theme.palette.primary.main, 0.06) },
      }}
    >
      <NotificationTabLabel tabId={tabId} label={label} />
      <Typography
        sx={{
          fontSize: 11,
          px: hasUnreadHighlight ? 0.75 : 0,
          borderRadius: 4,
          bgcolor: hasUnreadHighlight ? 'error.main' : 'transparent',
          color: hasUnreadHighlight ? 'error.contrastText' : 'inherit',
        }}
      >
        {formatHistoryCountDisplay(count)}
      </Typography>
    </ButtonBase>
  )
}

/**
 * Notification Center category tabs with per-tab counts.
 * @param {{ activeTab: string, summary: Record<string, number>|null, onChange: (tab: string) => void }} props
 */
export function NotificationTabs({ activeTab, summary, onChange }) {
  const { _ } = useLingui()

  return (
    <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5 }} role="tablist">
      {NOTIFICATION_TAB_ITEMS.map(tab => (
        <NotificationTabButton
          key={tab.id}
          tabId={tab.id}
          label={_(tab.label)}
          count={summary?.[tab.id] ?? 0}
          isActive={tab.id === activeTab}
          isUnreadTab={tab.id === NOTIFICATION_TABS.UNREAD}
          onClick={() => onChange(tab.id)}
        />
      ))}
    </Box>
  )
}
